import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { PaymentSource } from "features/payment-methods/types";

import { CUSTOMER_QUERY_KEYS } from "common/constants";
import { post } from "common/helpers/HTTP";

// Docs: https://apidocs.chargebee.com/docs/api/payment_intents
interface PaymentIntent {
  id: string;
  status: "inited" | "in_progress" | "authorized" | "consumed" | "expired";
}

async function getPaymentIntentRequest({
  brand,
  countryCode,
}: {
  brand: string;
  countryCode: string;
}): Promise<PaymentIntent> {
  return (
    await post("billing/payment-intents", {
      brand,
      country_code: countryCode,
    })
  ).payment_intent;
}

async function addPaymentSourceRequest(
  source: "card" | "paypal" | "apple-pay",
  token: string,
): Promise<PaymentSource> {
  return (await post(`billing/${source}`, { token })).payment_source;
}

async function addPaymentSourceFromIntentRequest(
  intentId: string,
): Promise<PaymentSource> {
  return (
    await post(`billing/payment-sources/from-intent`, {
      payment_intent_id: intentId,
    })
  ).payment_source;
}

export default function usePaymentSourcesMutations() {
  const queryClient = useQueryClient();

  const {
    mutateAsync: getPaymentIntent,
    isPending: isGettingPaymentIntent,
    error: getPaymentIntentError,
  } = useMutation({
    mutationFn: (params: { brand: string; countryCode: string }) =>
      getPaymentIntentRequest(params),
  });

  const {
    mutateAsync: addPaymentSource,
    isPending: isAddingPaymentSource,
    error: addPaymentSourceError,
  } = useMutation({
    mutationFn: ({
      token,
      source,
    }: {
      token: string;
      source: "paypal" | "apple-pay";
    }) => addPaymentSourceRequest(source, token),
    onSuccess: (paymentSource) => {
      queryClient.setQueryData(
        CUSTOMER_QUERY_KEYS.paymentSourceKeys.list(),
        (prevPaymentSources: PaymentSource[]) => [
          paymentSource,
          ...prevPaymentSources,
        ],
      );
    },
  });

  const {
    mutateAsync: addPaymentSourceFromIntent,
    isPending: isAddingPaymentSourceFromIntent,
    error: addPaymentSourceFromIntentError,
    reset: resetAddPaymentSourceFromIntentError,
  } = useMutation({
    mutationFn: ({ intentId }: { intentId: string }) =>
      addPaymentSourceFromIntentRequest(intentId),
    onSuccess: (paymentSource) => {
      queryClient.setQueryData(
        CUSTOMER_QUERY_KEYS.paymentSourceKeys.list(),
        (prevPaymentSources: PaymentSource[]) => [
          paymentSource,
          ...prevPaymentSources,
        ],
      );
    },
  });

  return {
    getPaymentIntent,
    isGettingPaymentIntent,
    getPaymentIntentError,
    addPaymentSource,
    isAddingPaymentSource,
    addPaymentSourceError,
    addPaymentSourceFromIntent,
    isAddingPaymentSourceFromIntent,
    addPaymentSourceFromIntentError,
    resetAddPaymentSourceFromIntentError,
  };
}
